import axios, { AxiosInstance } from 'axios';
import { API_CONFIG, ENDPOINTS } from '../config/api';

class ApiService {
  private api: AxiosInstance;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.baseURL,
      headers: API_CONFIG.headers,
      withCredentials: false, // Token auth doesn't need cookies
    });

    // Load tokens from localStorage
    this.loadTokens();

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers['Authorization'] = `Bearer ${this.accessToken}`;
        }
        // Remove Content-Type header for FormData requests
        // so browser can set it with the correct boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        if (error.response?.status === 401) {
          // Clear tokens and redirect to login
          this.clearTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
  }

  private loadTokens() {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
  }

  private saveTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  }

  private clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  // Authentication
  async login(email: string, password: string) {
    const response = await this.api.post('/api/token', {
      email,
      password,
    });

    if (response.data.access_token) {
      this.saveTokens(response.data.access_token, response.data.refresh_token);
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } else if (response.data.error) {
      throw new Error(response.data.error_description || 'Login failed');
    } else {
      throw new Error('Login failed');
    }
  }

  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.api.post('/api/token', {
      refresh_token: this.refreshToken,
    });

    if (response.data.access_token) {
      this.accessToken = response.data.access_token;
      localStorage.setItem('access_token', response.data.access_token);
      return response.data;
    } else {
      throw new Error(response.data.error_description || 'Token refresh failed');
    }
  }

  async logout() {
    if (this.accessToken) {
      try {
        await this.api.post('/api/revoke', {
          access_token: this.accessToken,
        });
      } catch (error) {
        // Token revocation failed, but continue with logout
      }
    }
    this.clearTokens();
  }

  // Bookings
  async getBookings() {
    const response = await this.api.post(ENDPOINTS.jsonrpc, {
      jsonrpc: '2.0',
      method: 'call',
      params: {
        model: 'appointment.booking',
        method: 'search_read',
        args: [[]],
        kwargs: {
          fields: [
            'id', 'name', 'display_name', 'subject', 'state', 'notes',
            'start_datetime', 'end_datetime', 'duration', 'user_id', 'location',
            'partner_id', 'external_attendee_name', 'external_attendee_email', 
            'external_attendee_phone', 'is_external', 'calendar_event_id'
          ],
          order: 'create_date desc',
        },
      },
      id: Math.floor(Math.random() * 1000000),
    });

    return response.data.result;
  }

  async getBooking(id: number) {
    const response = await this.api.post(ENDPOINTS.jsonrpc, {
      jsonrpc: '2.0',
      method: 'call',
      params: {
        model: 'appointment.booking',
        method: 'read',
        args: [[id], [
          'id', 'name', 'display_name', 'subject', 'state', 'notes',
          'start_datetime', 'end_datetime', 'duration', 'user_id', 'location',
          'partner_id', 'external_attendee_name', 'external_attendee_email', 
          'external_attendee_phone', 'is_external', 'calendar_event_id'
        ]],
        kwargs: {},
      },
      id: Math.floor(Math.random() * 1000000),
    });

    return response.data.result[0];
  }

  // Documents
  async uploadDocument(file: File, bookingId: number) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('appointment_id', bookingId.toString());
    formData.append('model', 'appointment.booking');
    formData.append('res_id', bookingId.toString());

    const response = await this.api.post(ENDPOINTS.documentUpload, formData);

    return response.data;
  }

  async getDocuments(bookingId: number) {
    const response = await this.api.post(ENDPOINTS.jsonrpc, {
      jsonrpc: '2.0',
      method: 'call',
      params: {
        model: 'documents.document',
        method: 'search_read',
        args: [[
          ['res_model', '=', 'appointment.booking'], 
          ['res_id', '=', bookingId],
          ['active', '=', true]  // Only get active documents
        ]],
        kwargs: {
          fields: ['id', 'name', 'file_name', 'create_date', 'file_type', 'is_signed', 'signed_date', 'attachment_url', 's3_key'],
          order: 'create_date desc',
        },
      },
      id: Math.floor(Math.random() * 1000000),
    });

    // Filter out documents without files and use attachment_url directly
    return response.data.result
      .filter((doc: any) => doc.s3_key || doc.attachment_url)
      .map((doc: any) => ({
        ...doc,
        document_url: doc.attachment_url // Use the presigned URL from backend
      }));
  }

  async signDocument(signatureData: string, bookingId: number) {
    const response = await this.api.post(ENDPOINTS.documentSign, {
      signature: signatureData,
      appointment_id: bookingId,
    });

    return response.data;
  }

  // Generic JSONRPC method
  async jsonrpc(model: string, method: string, args: any[] = [], kwargs: any = {}) {
    const response = await this.api.post(ENDPOINTS.jsonrpc, {
      jsonrpc: '2.0',
      method: 'call',
      params: {
        model,
        method,
        args,
        kwargs,
      },
      id: Math.floor(Math.random() * 1000000),
    });

    return response.data.result;
  }
}

const apiService = new ApiService();
export default apiService;